import LocaleSwitch from '@components/utils/localeSwitch';
import { StyledProps } from '@emotion/react';
import styled from '@emotion/styled';
import { useElementSize } from '@hooks/useElementSize';
import {
    DependencyList,
    ForwardedRef,
    ReactNode,
    forwardRef,
    useEffect,
    useRef,
    useState,
} from 'react';

interface Props {
    header: ReactNode;
    body: ReactNode;
    footer?: ReactNode;
    hasFixedHeader?: boolean;
    hasFixedFooter?: boolean;
    withBodyPadding?: boolean;
    footerHasPointerEvents?: boolean;
    scrollToTopDependencies?: DependencyList;
    bodyOverHeader?: boolean;
    liftBodyUnderHeader?: boolean;
    locales?: string[];
}

interface HeaderProps {
    hasFixedHeader?: boolean;
}

interface BodyProps {
    withBodyPadding?: boolean;
    bodyOverHeader?: boolean;
    liftBodyUnderHeader?: boolean;
    top?: string;
}

interface FooterProps {
    hasFixedFooter?: boolean;
    footerHasPointerEvents?: boolean;
}

const Container = styled.div<
    StyledProps<{ headerHeight: number | null; isSafari: boolean }>
>`
    height: 100%;
    overflow-x: hidden;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;
    align-items: center;
    @media (min-width: 748px) {
        border-radius: ${({ theme }) => theme.shape.radius.default};
        transform: ${({ isSafari }) => (isSafari ? 'translateZ(0)' : '')};
    }
    scroll-behavior: smooth;
    scroll-padding-top: ${({ headerHeight }) => headerHeight}px;
`;

const Header = styled.header<StyledProps<HeaderProps>>`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: ${({ hasFixedHeader }) =>
        hasFixedHeader ? 'sticky' : undefined};
    top: 0;
    z-index: 1000;
    @media (min-width: 748px) {
        border-top-left-radius: ${({ theme }) => theme.shape.radius.default};
        border-top-right-radius: ${({ theme }) => theme.shape.radius.default};
    }
`;

const LocaleSwitchDiv = styled.div<{ hasContent?: boolean }>`
    display: flex;
    justify-content: end;
    width: 100%;
    z-index: 9999;
    padding-block: ${(props) => props.theme.spacing.xs};
    padding-inline: ${(props) => props.theme.spacing.md};
    visibility: ${(props) => (props.hasContent ? 'visible' : 'hidden')};
`;

const Body = styled.div<BodyProps>`
    position: relative;
    width: 100%;
    padding-inline: ${(props) =>
        props.withBodyPadding ? props.theme.spacing.md : 0};
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    z-index: ${({ bodyOverHeader }) => (bodyOverHeader ? 1001 : 0)};
    top: ${({ top }) => top};
`;

const Footer = styled.footer<StyledProps<FooterProps>>`
    width: 100%;
    padding-top: ${({ theme }) => theme.spacing.xs};
    position: ${({ hasFixedFooter }) => (hasFixedFooter ? 'sticky' : 'static')};
    bottom: 0;
    @media (min-width: 748px) {
        border-bottom-left-radius: ${({ theme }) => theme.shape.radius.default};
        border-bottom-right-radius: ${({ theme }) =>
            theme.shape.radius.default};
    }
    z-index: 1000;
    pointer-events: ${({ footerHasPointerEvents }) =>
        footerHasPointerEvents ? 'auto' : 'none'};
`;

const AppLayout = (
    {
        header,
        body,
        footer,
        hasFixedHeader = false,
        hasFixedFooter = false,
        withBodyPadding = true,
        footerHasPointerEvents = true,
        scrollToTopDependencies,
        bodyOverHeader = false,
        locales = [],
        liftBodyUnderHeader = false,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>
) => {
    const [isSafari, setIsSafari] = useState(false);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [headerSize, headerRef] = useElementSize();

    useEffect(() => {
        const checkSafari = () => {
            const userAgent = window.navigator.userAgent;
            const safari =
                userAgent.includes('Safari') && !userAgent.includes('Chrome');
            setIsSafari(safari);
        };

        checkSafari();
    }, []);

    useEffect(() => {
        if (scrollToTopDependencies) {
            bodyRef.current?.scrollIntoView();
        }
    }, [scrollToTopDependencies]);

    return (
        <Container
            ref={ref}
            headerHeight={headerSize.height}
            isSafari={isSafari}
        >
            {locales?.length > 1 && (
                <LocaleSwitchDiv hasContent={locales?.length > 1}>
                    <LocaleSwitch subLocales={locales} />
                </LocaleSwitchDiv>
            )}
            <Header ref={headerRef} hasFixedHeader={hasFixedHeader}>
                {header}
            </Header>
            <Body
                ref={bodyRef}
                withBodyPadding={withBodyPadding}
                bodyOverHeader={bodyOverHeader}
                top={
                    liftBodyUnderHeader ? `-${headerSize.height}px` : undefined
                }
            >
                {body}
            </Body>
            <Footer
                hasFixedFooter={hasFixedFooter}
                footerHasPointerEvents={footerHasPointerEvents}
            >
                {footer}
            </Footer>
        </Container>
    );
};

export default forwardRef(AppLayout);
